import { useState } from "react";
import { api } from "@/data/api";
import { npmApiErrorMessage } from "@/data/npmState";
import { useAsync } from "@/hooks/useAsync";
import { SERVICE_KIND_LABEL } from "@/lib/health";
import { AsyncBoundary } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Adotar = registrar ownership de um recurso já existente (nada é copiado nem
// alterado no Docker). Só recursos atuais e sem dono são elegíveis.
export function AddServiceDialog({ projectId, open, onClose, onAdopted }) {
  const [mode, setMode] = useState("adopt");
  const [selected, setSelected] = useState([]);
  const [reviewing, setReviewing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const q = useAsync(() => (open ? api.getProjectAdoptionCandidates(projectId) : Promise.resolve(null)), [open, projectId]);
  const candidates = q.data?.candidates || [];
  const chosen = candidates.filter((c) => selected.includes(c.id));
  const toggle = (id) => setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const close = () => { setSelected([]); setReviewing(false); setError(null); onClose(); };
  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.adoptProjectServices(projectId, selected);
      onAdopted();
      close();
    } catch (e) {
      setError(npmApiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return <Dialog open={open} onOpenChange={(o) => !o && close()}>
    <DialogContent className="max-w-2xl border-line bg-popover">
      <DialogHeader>
        <DialogTitle>Adicionar serviço</DialogTitle>
        <DialogDescription>O serviço passa a pertencer a este Projeto e aparece automaticamente no Overview. Nenhum container é alterado.</DialogDescription>
      </DialogHeader>
      <div className="flex gap-2">
        <Button size="sm" variant={mode === "adopt" ? "default" : "outline"} onClick={() => setMode("adopt")}>Adotar serviço existente</Button>
        <Button size="sm" variant="outline" disabled title="Ainda não implementado">Criar novo serviço (em breve)</Button>
      </div>
      <AsyncBoundary loading={q.loading} error={q.error}>
        {!reviewing ? <div className="max-h-80 space-y-1 overflow-y-auto" data-testid="adopt-candidates">
          {candidates.length ? candidates.map((c) => <label key={c.id} className="flex cursor-pointer items-start gap-3 rounded-lg border border-line px-3 py-2 text-sm hover:bg-white/[0.02]">
            <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} className="mt-1" />
            <span className="min-w-0 flex-1">
              <span className="font-mono text-xs font-medium text-foreground">{c.name}</span>
              <span className="ml-2 text-xs text-muted-foreground">{SERVICE_KIND_LABEL[c.kind] || c.kind}{c.engine ? ` · ${c.engine}` : ""} · {c.state}</span>
              <span className="block text-[11px] text-muted-foreground">{c.compose_project ? `stack ${c.compose_project}/${c.compose_service || "—"}` : "sem stack compose"}</span>
              {c.derivable && <span className="block text-[11px] text-ok">Mesmo stack de {c.evidence.anchor_container} (evidência estrutural)</span>}
            </span>
          </label>) : <p className="text-sm text-muted-foreground">Nenhum recurso elegível (atual e sem Projeto) nos servidores deste Projeto.</p>}
        </div> : <div className="space-y-2 text-sm" data-testid="adopt-review">
          <p>Confirme a adoção de {chosen.length} serviço(s) para este Projeto:</p>
          <ul className="list-disc pl-5 font-mono text-xs">{chosen.map((c) => <li key={c.id}>{c.name}</li>)}</ul>
        </div>}
      </AsyncBoundary>
      {error && <p role="alert" className="rounded-lg border border-err/30 bg-err/10 px-3 py-2 text-sm text-err">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={reviewing ? () => setReviewing(false) : close}>{reviewing ? "Voltar" : "Cancelar"}</Button>
        {!reviewing ? <Button size="sm" disabled={!selected.length} onClick={() => setReviewing(true)}>Revisar ({selected.length})</Button>
          : <Button size="sm" disabled={busy} onClick={confirm} data-testid="adopt-confirm">{busy ? "Adotando…" : "Confirmar adoção"}</Button>}
      </div>
    </DialogContent>
  </Dialog>;
}
